import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Search, DollarSign, Download, Mail } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

export default function CustomerReceiptsPage() {
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [form, setForm] = useState({ customer_id: '', customer_name: '', invoice_id: '', date: localToday(), amount: '', payment_mode: 'cash', bank_reference: '', notes: '' });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [r, c, inv] = await Promise.all([api.get('/customer-receipts'), api.get('/customers'), api.get('/invoices?limit=5000')]);
      setDocs(r.data || []); setCustomers(c.data?.data || c.data || []); setInvoices(inv.data?.data || inv.data || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const save = async () => {
    try {
      if (editDoc) await api.put(`/customer-receipts/${editDoc.id}`, form);
      else await api.post('/customer-receipts', form);
      toast.success(editDoc ? 'Updated' : 'Receipt recorded'); setShowForm(false); setEditDoc(null); fetchData();
    } catch { toast.error('Save failed'); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/customer-receipts/${id}`); toast.success('Deleted'); fetchData(); };
  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/customer-receipts/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `receipt_${id.slice(0, 8)}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
  };
  const sendEmail = async () => {
    if (!emailTo || !emailDialog) return;
    try {
      const r = await api.post(`/customer-receipts/${emailDialog}/email`, { email: emailTo });
      if (r.data?.success) { toast.success(r.data.message); setEmailDialog(null); setEmailTo(''); }
      else toast.error(r.data?.error || 'Send failed');
    } catch { toast.error('Email send failed'); }
  };
  const openNew = () => { setEditDoc(null); setForm({ customer_id: '', customer_name: '', invoice_id: '', date: localToday(), amount: '', payment_mode: 'cash', bank_reference: '', notes: '' }); setShowForm(true); };
  const openEdit = (d) => { setEditDoc(d); setForm({ customer_id: d.customer_id, customer_name: d.customer_name, invoice_id: d.invoice_id || '', date: d.date, amount: d.amount, payment_mode: d.payment_mode || 'cash', bank_reference: d.bank_reference || '', notes: d.notes || '' }); setShowForm(true); };

  const filtered = docs.filter(d => !search || d.reference_id?.toLowerCase().includes(search.toLowerCase()) || d.customer_name?.toLowerCase().includes(search.toLowerCase()));
  const totalReceived = docs.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="customer-receipts-page">
        <div className="flex items-center justify-between">
          <div><h1 className="text-xl font-bold text-foreground">Customer Receipts</h1><p className="text-sm text-muted-foreground">Record payments received from customers</p></div>
          <Button onClick={openNew} data-testid="create-receipt-btn"><Plus size={16} className="mr-1" /> Record Receipt</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><DollarSign size={18} className="text-success" /></div><div><p className="text-xs text-muted-foreground">Total Received</p><p className="text-lg font-bold text-success">{totalReceived.toLocaleString('en', { minimumFractionDigits: 2 })}</p></div></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Receipts</p><p className="text-lg font-bold">{docs.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">This Month</p><p className="text-lg font-bold">{docs.filter(d => d.date?.startsWith(new Date().toISOString().slice(0, 7))).length}</p></CardContent></Card>
        </div>
        <div className="relative max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b"><tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Payment Mode</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No receipts yet</td></tr> :
                 filtered.map(d => (
                  <tr key={d.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{d.reference_id}</td>
                    <td className="px-4 py-3">{d.customer_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.date}</td>
                    <td className="px-4 py-3"><Badge className="bg-info/10 text-info">{d.payment_mode}</Badge></td>
                    <td className="px-4 py-3 text-right font-medium text-success">{(d.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} className="text-xs h-7">Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(d.id)} className="text-xs h-7 text-orange-600" data-testid={`receipt-pdf-${d.id}`}><Download size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEmailDialog(d.id); setEmailTo(''); }} className="text-xs h-7 text-info" data-testid={`receipt-email-${d.id}`}><Mail size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(d.id)} className="text-xs h-7 text-error"><Trash2 size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editDoc ? 'Edit' : 'Record'} Customer Receipt</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Customer</label>
              <Select value={form.customer_id} onValueChange={v => { const c = customers.find(x => x.id === v); setForm({...form, customer_id: v, customer_name: c?.name || ''}); }}>
                <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.ref ? ` (${c.ref})` : ''}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Related Invoice</label>
              <Select value={form.invoice_id} onValueChange={v => setForm({...form, invoice_id: v})}>
                <SelectTrigger><SelectValue placeholder="Optional - link to invoice" /></SelectTrigger>
                <SelectContent>{invoices.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number || inv.reference_id}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Amount</label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Payment Mode</label>
                <Select value={form.payment_mode} onValueChange={v => setForm({...form, payment_mode: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Bank Reference</label><Input value={form.bank_reference} onChange={e => setForm({...form, bank_reference: e.target.value})} placeholder="Ref #" /></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button><Button onClick={save} data-testid="save-receipt-btn">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!emailDialog} onOpenChange={() => setEmailDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Send via Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@email.com" type="email" data-testid="email-input" />
            <p className="text-xs text-muted-foreground">PDF will be attached to the email</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailDialog(null)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={!emailTo} data-testid="send-email-btn"><Mail size={14} className="mr-1" /> Send</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
